// Versão: 1.00
// Data..: 2026-09-24
// Utilidades de árvore de categorias (categoria → subcategoria → tipo).

import type { Category } from '@makweb/shared';

// Opção achatada para uso em <select>, com indentação por nível.
export interface CategoryOption {
  id: string;
  label: string; // com prefixo de indentação
  path: string; // caminho completo "Pai › Filho › Neto"
  depth: number;
}

// Achata a árvore em uma lista ordenada (pré-ordem) para exibição em selects.
// Apenas categorias ativas entram por padrão.
export function buildCategoryOptions(
  all: Category[],
  opts: { includeInactive?: boolean } = {},
): CategoryOption[] {
  const active = opts.includeInactive ? all : all.filter((c) => c.active);
  const byParent = new Map<string, Category[]>();
  for (const c of active) {
    const key = c.parentId ?? '__root__';
    const list = byParent.get(key) ?? [];
    list.push(c);
    byParent.set(key, list);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  }

  const out: CategoryOption[] = [];
  const walk = (parentKey: string, depth: number, prefix: string) => {
    for (const c of byParent.get(parentKey) ?? []) {
      const path = prefix ? `${prefix} › ${c.name}` : c.name;
      out.push({
        id: c.id,
        label: `${'\u00A0\u00A0'.repeat(depth)}${c.name}`,
        path,
        depth,
      });
      walk(c.id, depth + 1, path);
    }
  };
  walk('__root__', 0, '');
  return out;
}

// Resolve o caminho completo de uma categoria pelo id.
export function categoryPath(all: Category[], id?: string): string | undefined {
  if (!id) return undefined;
  const byId = new Map(all.map((c) => [c.id, c]));
  const parts: string[] = [];
  let cur = byId.get(id);
  let guard = 0;
  while (cur && guard++ < 20) {
    parts.unshift(cur.name);
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  return parts.length ? parts.join(' › ') : undefined;
}
